import { type FC, type ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router"
import { getCookie } from "react-use-cookie"

import { toNullIfEmpty } from "@forento/core/utilities/string"

import { useAlert } from "~/contexts/AlertContext"
import { useUser } from "~/contexts/UserContext"
import trpc from "~/utilities/trpc"

import GoogleSignInContext, { type GoogleSignInContextValue } from "."

const GoogleSignInContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const user = useUser()
	const [searchParams] = useSearchParams()
	const alert = useAlert()

	const [isEnabled, setEnabled] = useState(true)
	const [isLoaded, setLoaded] = useState(false)

	const load = useCallback(() => {
		window.google.accounts.id.initialize({
			use_fedcm_for_prompt: true,
			client_id: "865250728390-rnqcl33tik70lm18r9vruj41791m2l1j.apps.googleusercontent.com",
			cancel_on_tap_outside: false,
			callback: async ({ credential }) => {
				if (user.user !== undefined) {
					const response = await trpc.user.connectGoogle.mutate({ credential })
					if (response.status === "not-same-email") {
						alert.show("Error", "You can't connect a Google account with a different email address.")
					} else if (response.status === "already-connected") {
						alert.show("Error", "That Google account is already associated with an account.")
					} else if (response.status === "success") {
						await user.reload()
					}
				} else {
					const customName = await (async () => {
						const verification = await trpc.user.verifyGoogleCredential.query(credential)
						if (verification.status === "incomplete-name") {
							const dialog = await alert.multipleInputs(
								"Complete your account",
								"Please complete your account.",
								[
									{ label: "First name", defaultValue: verification.firstName },
									{ label: "Last name", defaultValue: verification.lastName },
								],
							)
							if (!dialog.result) return undefined
							dialog.close()
							return { first: dialog.result[0], last: dialog.result[1] }
						}
						return null
					})()
					if (customName === undefined) return

					const response = await trpc.user.loginGoogle.mutate({
						credential,
						source: searchParams.get("s") ?? toNullIfEmpty(getCookie("s")),
						referralCode: searchParams.get("r"),
						name: customName ?? undefined,
					})

					if (response.status === "social-not-connected") {
						await alert.show(
							"Error",
							"Your Google account is not connected to your Forento account. Please connect Google in your Forento account settings.",
						)
					} else if (response.status === "invalid-referral-code") {
						await alert.show("Error", "That referral code is invalid.")
					} else if (response.status === "success") {
						await user.reload({ clearSessionCache: true })
					}
				}
			},
		})

		setLoaded(true)

		return () => {
			setLoaded(false)
			window.google.accounts.id.cancel()
		}
	}, [alert, searchParams, user])

	useEffect(() => {
		window.google?.accounts?.id?.cancel()

		if (!isEnabled) return

		if (window.google?.accounts?.id !== undefined) {
			load()
		} else {
			const interval = setInterval(() => {
				if (window.google?.accounts?.id === undefined) return
				clearInterval(interval)
				load()
			}, 1000)

			return () => {
				clearInterval(interval)
			}
		}
	}, [isEnabled, load])

	useEffect(() => {
		if (user.isLoading || user.user !== undefined || !isLoaded) return

		window.google?.accounts?.id?.prompt()
	}, [user, isLoaded])

	const value = useMemo<GoogleSignInContextValue>(
		() => ({
			isInitialized: isLoaded,
			disable() {
				setEnabled(false)
			},
		}),
		[isLoaded],
	)

	return <GoogleSignInContext.Provider value={value}>{children}</GoogleSignInContext.Provider>
}

export default GoogleSignInContextProvider
